<template>
  <div
    class="question-field rounded-lg pa-2"
    :class="{
      required: question.isRequired,
      'bg-gray-50': !question.isRequired,
      'bg-blue-50': question.isRequired
    }"
  >
    <!-- Question Header with Icon -->
    <div class="question-header mb-2 d-flex align-start">
      <v-icon class="mr-2 mt-1" :color="getQuestionIconColor(question.questionType)">
        <!-- {{ getQuestionIcon(question.questionType) }} -->
      </v-icon>
      <div>
        <h4 class="text-body-1 font-weight-medium mb-1">
          {{ question.question }}
          <span v-if="question.isRequired" class="required-asterisk">*</span>
        </h4>
        <p v-if="question.validationMessage" class="text-caption text--secondary mb-0">
          {{ question.validationMessage }}
        </p>
      </div>
    </div>

    <!-- All Form Fields Container -->
    <div class="preview-container">
      <!-- Text Input -->
      <v-text-field
        v-if="question.questionType === 'TEXT'"
        v-model="localResponse"
        :label="placeholderText"
        outlined
        dense
        :disabled="disabled"
        :required="question.isRequired"
        :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-form-textbox"
      ></v-text-field>

      <!-- Textarea -->
      <v-textarea
        v-else-if="question.questionType === 'TEXTAREA'"
        v-model="localResponse"
        :label="placeholderText"
        outlined
        dense
        :disabled="disabled"
        :required="question.isRequired"
        :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
        rows="3"
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-text-box-outline"
      ></v-textarea>

      <!-- Number Input -->
      <v-text-field
        v-else-if="question.questionType === 'NUMBER'"
        v-model.number="localResponse"
        :label="placeholderText"
        outlined
        dense
        type="number"
        :disabled="disabled"
        :required="question.isRequired"
        :rules="
          question.isRequired
            ? [(v) => (v !== null && v !== undefined) || 'This field is required']
            : []
        "
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-numeric"
      ></v-text-field>

      <!-- Checkbox Group -->
      <div v-else-if="question.questionType === 'CHECKBOX'">
        <v-checkbox
          v-for="(option, i) in question.options"
          :key="i"
          v-model="checkboxResponses[option]"
          :label="option"
          dense
          :disabled="disabled"
          hide-details="auto"
          prepend-inner-icon="mdi-checkbox-marked-outline"
          class="mt-0 mb-1"
          @change="updateCheckboxResponse"
        >
          <!-- <template v-slot:prepend>
            <v-icon small class="mr-2">mdi-checkbox-marked-outline</v-icon>
          </template> -->
        </v-checkbox>
      </div>

      <!-- Radio Group -->
      <div v-else-if="question.questionType === 'RADIO'">
        <v-radio-group
          v-model="localResponse"
          :required="question.isRequired"
          :disabled="disabled"
          :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
          hide-details="auto"
          class="mt-0"
        >
          <v-radio
            v-for="(option, i) in question.options"
            :key="i"
            :label="option"
            :value="option"
            dense
            prepend-inner-icon="mdi-radiobox-marked"
            class="mt-0 mb-1"
          >
            <!-- <template v-slot:prepend>
              <v-icon small class="mr-2">mdi-radiobox-marked</v-icon>
            </template> -->
          </v-radio>
        </v-radio-group>
      </div>

      <!-- Select Options -->
      <v-select
        v-else-if="question.questionType === 'SELECT_OPTIONS'"
        v-model="localResponse"
        :items="question.options"
        :label="placeholderText"
        outlined
        dense
        :disabled="disabled"
        :required="question.isRequired"
        :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-form-dropdown"
      ></v-select>

      <!-- File Upload -->
      <div v-else-if="question.questionType === 'LOCAL_ATTACHMENTS'">
        <v-file-input
          v-model="fileResponse"
          :label="placeholderText"
          outlined
          dense
          :disabled="disabled"
          :multiple="question.allowedNoOfAttachments > 1"
          :counter="question.allowedNoOfAttachments"
          hide-details="auto"
          class="mb-2"
          prepend-inner-icon="mdi-file-upload-outline"
          @change="handleFileUpload"
        ></v-file-input>
        <div v-if="uploadedFiles.length > 0" class="uploaded-files mt-2">
          <v-chip
            v-for="(file, index) in uploadedFiles"
            :key="index"
            small
            close
            @click:close="removeFile(index)"
            class="mr-2 mb-2"
          >
            <v-icon left small>mdi-file-document-outline</v-icon>
            {{ file.name }}
          </v-chip>
        </div>
      </div>

      <!-- Media Capture - Simplified for demo -->
      <div
        v-else-if="
          ['CLICK_PICTURES', 'AUDIO_CAPTURE', 'VIDEO_CAPTURE'].includes(question.questionType)
        "
      >
        <v-btn
          outlined
          block
          class="mb-2"
          @click="captureMedia(question.questionType)"
          :disabled="disabled"
        >
          <v-icon left>
            {{
              question.questionType === 'CLICK_PICTURES'
                ? 'mdi-camera'
                : question.questionType === 'AUDIO_CAPTURE'
                ? 'mdi-microphone'
                : 'mdi-video'
            }}
          </v-icon>
          Capture
          {{
            question.questionType === 'CLICK_PICTURES'
              ? 'Photo'
              : question.questionType === 'AUDIO_CAPTURE'
              ? 'Audio'
              : 'Video'
          }}
        </v-btn>
        <div v-if="mediaResponse" class="media-preview mt-2">
          <v-chip small close @click:close="clearMedia">
            <v-icon left small>mdi-file</v-icon>
            {{ mediaResponse.name || 'Captured Media' }}
          </v-chip>
        </div>
      </div>

      <!-- Service Plan -->
      <div v-else-if="question.questionType === 'SERVICE_PLAN'" class="service-plan-section">
        <div v-if="question.payment_Plan && question.payment_Plan.length > 0">
          <h5 class="text-subtitle-1 font-weight-medium mb-2">Available Plans</h5>
          <div
            v-for="(plan, planIndex) in question.payment_Plan"
            :key="planIndex"
            class="mb-3 p-3 border rounded-lg bg-white"
          >
            <div class="font-weight-medium">{{ plan.planName }}</div>
            <div class="text-caption text--secondary">
              {{ plan.planDescription }}
            </div>
            <div class="d-flex justify-space-between mt-2">
              <span class="text-caption">Duration: {{ plan.planDuration }} days</span>
              <span class="font-weight-medium">Amount: {{ plan.planAmount }}</span>
            </div>
          </div>
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <div v-if="question.options && question.options.length > 0">
              <img
                :src="question.options[0]"
                class="mx-auto h-40 object-contain mb-2"
                alt="Payment QR"
              />
              <p class="text-caption text--secondary">Scan QR code to make payment</p>
            </div>
            <div v-else>
              <v-icon size="32" color="grey lighten-1">mdi-qrcode</v-icon>
              <p class="text-caption text--secondary">Payment QR code will appear here</p>
            </div>
          </div>
        </div>
        <div v-else>
          <v-icon size="32" color="grey lighten-1">mdi-currency-usd</v-icon>
          <p class="text-caption text--secondary">Payment plans will appear here</p>
        </div>
      </div>

      <!-- Data Category -->
      <v-select
        v-else-if="question.questionType === 'DATA_CATEGORY'"
        v-model="localResponse"
        :items="question.options || []"
        :label="placeholderText"
        outlined
        dense
        :disabled="disabled"
        :required="question.isRequired"
        :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-database"
      ></v-select>

      <!-- Date Input -->
      <v-menu
        v-else-if="question.questionType === 'DATE'"
        v-model="dateMenu"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="localResponse"
            :label="placeholderText"
            prepend-inner-icon="mdi-calendar"
            readonly
            outlined
            dense
            :disabled="disabled"
            v-bind="attrs"
            v-on="on"
            :required="question.isRequired"
            :rules="question.isRequired ? [(v) => !!v || 'This field is required'] : []"
            hide-details="auto"
            class="mb-2"
          ></v-text-field>
        </template>
        <v-date-picker v-model="localResponse" @input="dateMenu = false"></v-date-picker>
      </v-menu>

      <!-- Default fallback for unhandled question types -->
      <v-text-field
        v-else
        v-model="localResponse"
        :label="placeholderText"
        outlined
        dense
        :disabled="disabled"
        hide-details="auto"
        class="mb-2"
        prepend-inner-icon="mdi-help-circle-outline"
      ></v-text-field>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionField',
  props: {
    question: {
      type: Object,
      required: true
    },
    response: {
      type: [String, Number, Array, Object, File],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localResponse: this.response,
      checkboxResponses: this.initializeCheckboxResponses(),
      fileResponse: null,
      uploadedFiles: [],
      mediaResponse: null,
      dateMenu: false
    }
  },
  computed: {
    placeholderText() {
      return this.question.question || 'Your answer'
    }
  },
  watch: {
    response: {
      handler(newVal) {
        // Handle different data types for localResponse based on question type
        this.localResponse = this.processResponseForQuestionType(newVal, this.question.questionType)

        if (this.question.questionType === 'CHECKBOX') {
          this.$nextTick(() => {
            this.checkboxResponses = this.initializeCheckboxResponses()
          })
        }

        // Handle special cases for specific question types
        if (this.question.questionType === 'LOCAL_ATTACHMENTS' && Array.isArray(newVal)) {
          this.uploadedFiles = newVal
        } else if (
          ['CLICK_PICTURES', 'AUDIO_CAPTURE', 'VIDEO_CAPTURE'].includes(
            this.question.questionType
          ) &&
          Array.isArray(newVal) &&
          newVal.length > 0
        ) {
          this.mediaResponse = newVal[0]
        }
      },
      immediate: true
    },
    localResponse(newVal) {
      // Only emit for non-checkbox questions
      if (this.question.questionType !== 'CHECKBOX') {
        this.emitResponse(newVal)
      }
    },
    checkboxResponses: {
      handler() {
        // Emit response when checkbox responses change
        if (this.question.questionType === 'CHECKBOX') {
          this.updateCheckboxResponse()
        }
      },
      deep: true
    }
  },
  methods: {
    /**
     * Process response data based on question type
     * @param {*} response - The response data
     * @param {string} questionType - The type of question
     * @returns {*} - Processed response value
     */
    processResponseForQuestionType(response, questionType) {
      // Handle array responses by taking the first element
      if (Array.isArray(response) && response.length > 0) {
        return this.processResponseForQuestionType(response[0], questionType)
      }

      // Handle stringified data
      if (typeof response === 'string') {
        // Handle stringified objects and arrays
        if (
          (response.startsWith('{') && response.endsWith('}')) ||
          (response.startsWith('[') && response.endsWith(']'))
        ) {
          try {
            const parsed = JSON.parse(response)
            if (Array.isArray(parsed) && parsed.length > 0) {
              return this.processResponseForQuestionType(parsed, questionType)
            }
            return parsed
          } catch (e) {
            // If parsing fails, use the string as is
          }
        }

        // Handle NUMBER type questions
        if (questionType === 'NUMBER') {
          const numValue = parseFloat(response)
          return isNaN(numValue) ? null : numValue
        }
      }

      // Handle numeric values for NUMBER type questions
      if (questionType === 'NUMBER' && typeof response === 'number') {
        return response
      }

      // Return the response as is for other cases
      return response
    },

    initializeCheckboxResponses() {
      const responses = {}
      if (this.question.questionType === 'CHECKBOX' && this.question.options) {
        this.question.options.forEach((option) => {
          // Check if this option was previously selected
          // Handle different response formats
          if (Array.isArray(this.response)) {
            // If response is an array of selected options
            responses[option] = this.response.some((resp) => {
              if (typeof resp === 'string') {
                return resp === option
              } else if (typeof resp === 'object' && resp !== null) {
                try {
                  return JSON.stringify(resp) === option || resp.toString() === option
                } catch (e) {
                  return false
                }
              }
              return false
            })
          } else if (typeof this.response === 'string') {
            // If response is a string, check if it's a stringified array
            if (this.response.startsWith('[') && this.response.endsWith(']')) {
              try {
                const parsedArray = JSON.parse(this.response)
                if (Array.isArray(parsedArray)) {
                  return parsedArray.includes(option)
                }
              } catch (e) {
                return this.response === option
              }
            }
            // If response is a string, check if it matches the option
            responses[option] = this.response === option
          } else if (typeof this.response === 'object' && this.response !== null) {
            // If response is an object with option keys
            responses[option] = !!this.response[option]
          } else {
            // Default to false if no previous response
            responses[option] = false
          }
        })
      }
      return responses
    },
    updateCheckboxResponse() {
      // Convert checkbox responses to array of selected values
      const selected = Object.keys(this.checkboxResponses).filter(
        (key) => this.checkboxResponses[key]
      )
      this.emitResponse(selected)
    },
    handleFileUpload(files) {
      if (files) {
        if (Array.isArray(files)) {
          this.uploadedFiles = files
        } else {
          this.uploadedFiles = [files]
        }
      } else {
        this.uploadedFiles = []
      }
      this.emitResponse(this.uploadedFiles)
    },
    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
      this.emitResponse(this.uploadedFiles)
    },
    captureMedia(type) {
      // In a real app, this would integrate with device APIs
      // For demo purposes, we'll simulate with a mock file
      this.mediaResponse = {
        name: `captured-${type.toLowerCase()}-${Date.now()}.mock`,
        type: type,
        timestamp: new Date()
      }
      this.emitResponse(this.mediaResponse)
    },
    clearMedia() {
      this.mediaResponse = null
      this.emitResponse(null)
    },
    emitResponse(value) {
      this.$emit('update:response', value)
    },
    getQuestionIcon(questionType) {
      const iconMap = {
        TEXT: 'mdi-form-textbox',
        TEXTAREA: 'mdi-text-box-outline',
        NUMBER: 'mdi-numeric',
        CHECKBOX: 'mdi-checkbox-marked-outline',
        RADIO: 'mdi-radiobox-marked',
        SELECT_OPTIONS: 'mdi-form-dropdown',
        LOCAL_ATTACHMENTS: 'mdi-file-upload-outline',
        CLICK_PICTURES: 'mdi-camera',
        AUDIO_CAPTURE: 'mdi-microphone',
        VIDEO_CAPTURE: 'mdi-video',
        SERVICE_PLAN: 'mdi-currency-usd',
        DATA_CATEGORY: 'mdi-database',
        DATE: 'mdi-calendar',
        DEFAULT: 'mdi-help-circle-outline'
      }

      return iconMap[questionType] || iconMap.DEFAULT
    },

    getQuestionIconColor(questionType) {
      const colorMap = {
        TEXT: 'blue',
        TEXTAREA: 'green',
        NUMBER: 'orange',
        CHECKBOX: 'teal',
        RADIO: 'amber',
        SELECT_OPTIONS: 'purple',
        LOCAL_ATTACHMENTS: 'indigo',
        CLICK_PICTURES: 'pink',
        AUDIO_CAPTURE: 'red',
        VIDEO_CAPTURE: 'deep-purple',
        SERVICE_PLAN: 'light-blue',
        DATA_CATEGORY: 'cyan',
        DATE: 'blue'
      }

      return colorMap[questionType] || 'grey'
    }
  }
}
</script>

<style scoped>
.question-field {
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;
}

.question-field.required .question-header h4 {
  font-weight: 600;
}

.required-asterisk {
  color: #f44336;
}

.media-preview {
  min-height: 40px;
}

.service-plan-section {
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 16px;
}

.preview-container {
  transition: all 0.3s ease;
}

.preview-container:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Add hover effect to the entire question field */
.question-field:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e0;
}
</style>
